import React from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import CardTabs, { type CardTab } from '../../common/CardTabs';
import LibraryFilterSheet, { LibraryFilterSheetField, LibraryFilterSheetGroup } from '../../common/LibraryFilterSheet';
import UserAvatar from '../../../ui/UserAvatar';
import { CloseIcon } from '../../../ui/CloseIcon';
import { LibraryFilterToggle } from '../../../ui/LibraryFilterToggle';
import { ModalActionError } from '../../../ui/ModalActionError';
import useCompactToolbar from '../../../hooks/useCompactToolbar';
import './DirectoryKit.css';

/**
 * Maintenance Directory kit: the User Directory's list surface (folder tabs, panel, toolbar,
 * table, footer), shared by every Maintenance hub so each Directory layout reads the same.
 * Data-agnostic: hubs pass their own rows, filters and actions.
 * See docs/maintenance-directory-layouts.md.
 */

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

/* ------------------------------------------------------------------ tabs + panel */

export type DirectoryTab<K extends string> = CardTab<K>;

/** The id CardTabs gives a tab, for the panel's aria-labelledby. */
export function directoryTabId(idPrefix: string, key: string): string {
  return `${idPrefix}-${key}-tab`;
}

/** Folder tabs that sit on top of a DirectoryPanel (the record-detail tab strip). */
export function DirectoryTabs<K extends string>({ tabs, value, onChange, label, idPrefix, panelId }: {
  tabs: ReadonlyArray<DirectoryTab<K>>;
  value: K;
  onChange: (key: K) => void;
  /** Names the tablist, e.g. "User directory views". */
  label: string;
  idPrefix: string;
  panelId: string;
}) {
  return (
    <CardTabs
      appearance="record"
      tabs={tabs}
      activeKey={value}
      onChange={onChange}
      ariaLabel={label}
      idPrefix={idPrefix}
      panelId={panelId}
      className="directory-tabs"
    />
  );
}

/**
 * The bordered card that holds a directory. With `tabs`, the tab strip sits on its top edge
 * and the selected tab joins its surface; pass `id`, `role="tabpanel"` and `aria-labelledby`.
 */
export function DirectoryPanel({ tabs, className, children, ...rest }: React.HTMLAttributes<HTMLDivElement> & {
  tabs?: React.ReactNode;
}) {
  const panel = <div {...rest} className={cx('directory-panel', className)}>{children}</div>;
  if (!tabs) return panel;
  return <div className="directory-shell directory-shell--tabbed">{tabs}{panel}</div>;
}

/* ------------------------------------------------------------------ toolbar */

/** Search, filters, the record count and the hub's actions, in one wrapping row. */
export function DirectoryToolbar({ children, className }: { children: React.ReactNode; className?: string }) {
  return <div className={cx('directory-toolbar', className)}>{children}</div>;
}

export const DirectorySearch = React.forwardRef<HTMLInputElement, {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Accessible name, e.g. "Search locations". */
  label: string;
}>(function DirectorySearch({ value, onChange, placeholder, label }, ref) {
  const innerRef = React.useRef<HTMLInputElement>(null);
  React.useImperativeHandle(ref, () => innerRef.current as HTMLInputElement);
  return (
    <div className="directory-search">
      <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 4 4" /></svg>
      <input ref={innerRef} type="search" aria-label={label} placeholder={placeholder} value={value} onChange={event => onChange(event.target.value)} />
      {value ? (
        <button type="button" className="directory-search__clear" aria-label="Clear search" onClick={() => { onChange(''); innerRef.current?.focus(); }}>
          <CloseIcon />
        </button>
      ) : null}
    </div>
  );
});

/** True inside the phone filter sheet that DirectoryFilters opens. */
const DirectoryFilterSheetContext = React.createContext(false);
/** True inside a DirectoryFilterGroup, whose label already names its controls. */
const DirectoryFilterGroupContext = React.createContext(false);

/**
 * A toolbar filter select. In the phone filter sheet it shows its `label` (or `aria-label`)
 * above it, like the other libraries' sheets.
 */
export function DirectoryFilter({ className, children, label, ...rest }: React.SelectHTMLAttributes<HTMLSelectElement> & {
  className?: string;
  /** Visible label in the phone filter sheet; defaults to `aria-label`. */
  label?: string;
}) {
  const inSheet = React.useContext(DirectoryFilterSheetContext);
  const inGroup = React.useContext(DirectoryFilterGroupContext);
  const select = <select {...rest} className={cx('control directory-filter', className)}>{children}</select>;
  const sheetLabel = label ?? rest['aria-label'];
  if (!inSheet || inGroup || !sheetLabel) return select;
  return <LibraryFilterSheetField label={sheetLabel}>{select}</LibraryFilterSheetField>;
}

/**
 * Toolbar controls that belong together, such as a sort select and its direction button. Inline
 * they render as they are; in the phone filter sheet they share one labelled row.
 */
export function DirectoryFilterGroup({ label, children }: { label: string; children: React.ReactNode }) {
  const inSheet = React.useContext(DirectoryFilterSheetContext);
  if (!inSheet) return <>{children}</>;
  return (
    <LibraryFilterSheetGroup label={label}>
      <DirectoryFilterGroupContext.Provider value>{children}</DirectoryFilterGroupContext.Provider>
    </LibraryFilterSheetGroup>
  );
}

/** A toolbar on/off filter, e.g. "Show inactive". */
export function DirectoryToggle({ checked, onChange, label, disabled }: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: string;
  disabled?: boolean;
}) {
  const inSheet = React.useContext(DirectoryFilterSheetContext);
  return (
    <label className={cx('directory-toggle', inSheet && 'directory-toggle--sheet')}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={event => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

/**
 * A hub's filters (DirectoryFilter and DirectoryToggle). Inline in the toolbar on wider screens;
 * on phones (≤720px, useCompactToolbar) a Filters button with the active count beside the
 * search, which opens the shared filter sheet (LibraryFilterSheet) with each filter labelled,
 * Reset and "Show N …". Filters apply as they change, as they do inline.
 */
export function DirectoryFilters({ children, activeCount, onReset, resultLabel }: {
  children: React.ReactNode;
  /** Filters that differ from their defaults; the button's badge. */
  activeCount: number;
  /** Puts every filter back to its default; Reset in the sheet. */
  onReset?: () => void;
  /** The sheet's footer button, e.g. "Show 12 locations". */
  resultLabel: string;
}) {
  const compact = useCompactToolbar();
  const [open, setOpen] = React.useState(false);
  const toggleRef = React.useRef<HTMLButtonElement>(null);
  React.useEffect(() => {
    if (!compact) setOpen(false);
  }, [compact]);
  if (!compact) return <>{children}</>;
  return (
    <>
      <LibraryFilterToggle
        ref={toggleRef}
        className="directory-filter-toggle"
        expanded={open}
        activeCount={activeCount}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      />
      <LibraryFilterSheet
        open={open}
        onClose={() => setOpen(false)}
        onReset={onReset}
        resetDisabled={activeCount === 0}
        resultLabel={resultLabel}
        returnFocusRef={toggleRef}
      >
        <DirectoryFilterSheetContext.Provider value>{children}</DirectoryFilterSheetContext.Provider>
      </LibraryFilterSheet>
    </>
  );
}

/** "12 of 82 locations"; pushes the actions after it to the right edge. */
export function DirectoryCount({ children }: { children: React.ReactNode }) {
  return <span className="directory-count" role="status">{children}</span>;
}

export type DirectorySortOption = { value: string; label: string };

/** The phone and tablet sort control; the table's headers sort on wider screens. */
export function DirectoryMobileSort({ options, value, onChange, direction, onToggleDirection }: {
  options: ReadonlyArray<DirectorySortOption>;
  value: string;
  onChange: (value: string) => void;
  direction: 'asc' | 'desc';
  /** Left out when the chosen order has no direction (e.g. a recommended order); the button goes too. */
  onToggleDirection?: () => void;
}) {
  return (
    <div className="directory-mobile-sort">
      <label>Sort by
        <select className="control" value={value} onChange={event => onChange(event.target.value)}>
          {options.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
        </select>
      </label>
      {onToggleDirection ? (
        <button type="button" className="btn btn-outline sm" aria-label={`Sort ${direction === 'asc' ? 'descending' : 'ascending'}`} onClick={onToggleDirection}>
          {direction === 'asc' ? 'Ascending' : 'Descending'}
        </button>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ table */

export type DirectoryColumn<Row> = {
  id: string;
  header: string;
  /** Label above the value in the stacked (tablet/phone) layout; defaults to `header`. */
  label?: string;
  /** Makes the header a sort button for this key. */
  sortKey?: string;
  className?: string;
  /** `primary` and `actions` span the stacked card and drop their label. */
  role?: 'primary' | 'actions';
  render: (row: Row) => React.ReactNode;
};

function SortDirectionIcon({ direction }: { direction: 'asc' | 'desc' }) {
  return direction === 'asc' ? (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 19V5" />
      <path d="m5 12 7-7 7 7" />
    </svg>
  ) : (
    <svg aria-hidden="true" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 5v14" />
      <path d="m19 12-7 7-7-7" />
    </svg>
  );
}

/** A header cell; with `onSort` it becomes the sort button (aria-sort included). */
export function DirectorySortHeader({ label, sortKey, activeKey, direction, onSort, className }: {
  label: string;
  sortKey?: string;
  activeKey?: string | null;
  direction?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  className?: string;
}) {
  if (!sortKey || !onSort) return <th scope="col" className={cx('directory-th', className)}>{label}</th>;
  const active = activeKey === sortKey;
  const ariaSort = active ? (direction === 'desc' ? 'descending' : 'ascending') : 'none';
  const next = active && direction === 'asc' ? 'descending' : 'ascending';
  return (
    <th scope="col" className={cx('directory-th', className)} aria-sort={ariaSort}>
      <button type="button" className="directory-sort" onClick={() => onSort(sortKey)} title={`Sort by ${label}`} aria-label={`Sort by ${label}, ${next}`}>
        <span>{label}</span>
        <span className="directory-sort__indicator" aria-hidden="true">{active ? <SortDirectionIcon direction={direction ?? 'asc'} /> : null}</span>
      </button>
    </th>
  );
}

/**
 * The directory table. Above 1100px it is a table with sortable headers; at and below it each
 * row becomes a labelled two-column card (primary and actions cells span the card).
 */
export function DirectoryTable<Row>({ ariaLabel, columns, rows, rowKey, sortKey, sortDirection, onSort, className, rowClassName }: {
  ariaLabel: string;
  columns: ReadonlyArray<DirectoryColumn<Row>>;
  rows: ReadonlyArray<Row>;
  rowKey: (row: Row) => string;
  sortKey?: string | null;
  sortDirection?: 'asc' | 'desc';
  onSort?: (key: string) => void;
  /** Page hook for column widths, e.g. `admin-users-directory-table`. */
  className?: string;
  rowClassName?: (row: Row) => string | undefined;
}) {
  return (
    <div className="directory-table-scroll">
      <table className={cx('directory-table', className)} aria-label={ariaLabel}>
        <thead>
          <tr>
            {columns.map(column => (
              <DirectorySortHeader
                key={column.id}
                label={column.header}
                sortKey={column.sortKey}
                activeKey={sortKey}
                direction={sortDirection}
                onSort={onSort}
                className={cx(column.className, column.role ? `directory-col--${column.role}` : null)}
              />
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(row => (
            <tr key={rowKey(row)} className={rowClassName?.(row)}>
              {columns.map(column => (
                <td
                  key={column.id}
                  className={cx('directory-td', column.className, column.role ? `directory-col--${column.role}` : null)}
                  data-label={column.label ?? column.header}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/* ------------------------------------------------------------------ cells */

/** Initials-or-photo circle for people (and records with a picture). */
export function DirectoryAvatar({ name, src }: { name: string; src?: string | null }) {
  const initials = name.split(/\s+/).filter(Boolean).slice(0, 2).map(part => part[0]).join('').toLocaleUpperCase() || '?';
  return <UserAvatar className="directory-avatar" name={name} src={src} fallback={initials} />;
}

/** The first cell: the record's name (opens it) over one muted line of context. */
export function DirectoryIdentity({ title, meta, leading, onOpen, href, openLabel }: {
  title: React.ReactNode;
  meta?: React.ReactNode;
  leading?: React.ReactNode;
  /** Opens the record; renders the name as a button. */
  onOpen?: () => void;
  /** Opens the record by route; renders the name as a link. */
  href?: string;
  openLabel?: string;
}) {
  const name = onOpen
    ? <button type="button" className="directory-identity__name" onClick={onOpen} aria-label={openLabel}>{title}</button>
    : href
      ? <Link className="directory-identity__name" to={href} aria-label={openLabel}>{title}</Link>
      : <strong className="directory-identity__name">{title}</strong>;
  return (
    <div className="directory-identity">
      {leading}
      <div className="directory-identity__text">
        {name}
        {meta ? <span className="directory-identity__meta">{meta}</span> : null}
      </div>
    </div>
  );
}

export type DirectoryChipTone = 'neutral' | 'primary' | 'info' | 'outline' | 'success' | 'warning' | 'danger' | 'muted';

/** A compact label: `neutral` reads like the security level, `outline` like a workflow role. */
export function DirectoryChip({ tone = 'neutral', title, children }: {
  tone?: DirectoryChipTone;
  title?: string;
  children: React.ReactNode;
}) {
  return <span className="directory-chip" data-tone={tone} title={title}>{children}</span>;
}

export function DirectoryChips({ children }: { children: React.ReactNode }) {
  return <div className="directory-chips">{children}</div>;
}

/** Muted secondary line under a cell's value, e.g. "Secondary: Dennis Segrist" (small caps). */
export function DirectoryCellNote({ children }: { children: React.ReactNode }) {
  return <small className="directory-cell-note">{children}</small>;
}

/** Muted second line in normal case, for longer context such as a list of charge scopes. */
export function DirectoryCellMeta({ children, title }: { children: React.ReactNode; title?: string }) {
  return <span className="directory-cell-meta" title={title}>{children}</span>;
}

/**
 * The last cell: "Manage ›" plus the row's ⋯ menu (pass a MaintenanceActionsDropdown with
 * `menuOnly`). The menu slot keeps its width when empty so rows stay aligned.
 */
export function DirectoryRowActions({ label = 'Manage', name, onManage, href, disabled, title, menu, menuTitle = 'More actions' }: {
  label?: string;
  /** Record name for the accessible label, e.g. "Manage 000-Cincinnati Base". */
  name: string;
  onManage?: () => void;
  href?: string;
  disabled?: boolean;
  title?: string;
  menu?: React.ReactNode;
  menuTitle?: string;
}) {
  const chevron = <svg viewBox="0 0 24 24" aria-hidden="true"><path d="m9 5 7 7-7 7" /></svg>;
  const ariaLabel = `${label} ${name}`;
  return (
    <div className="directory-actions">
      {href && !disabled ? (
        <Link className="directory-manage" to={href} title={title ?? ariaLabel} aria-label={ariaLabel}>{label}{chevron}</Link>
      ) : (
        <button type="button" className="directory-manage" disabled={disabled || !onManage} title={title ?? ariaLabel} aria-label={ariaLabel} onClick={onManage}>
          {label}{chevron}
        </button>
      )}
      <span className="directory-actions__menu" title={menuTitle}>{menu}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ record dialog */

/**
 * "Manage ›" for hubs whose records have no full-page editor: the record's editor in a dialog,
 * titled with the record, with optional folder tabs and a footer. Focus, Tab and Escape come from
 * the Maintenance layout's dialog manager (useMaintenanceDialogFocus), which finds the Close button.
 */
export function DirectoryRecordDialog({ title, subtitle, status, tabs, footer, error, errorTitle, size = 'lg', onClose, closeDisabled, className, children }: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Usually an AutosaveStatus. */
  status?: React.ReactNode;
  /** A DirectoryTabs strip for the record's sections. */
  tabs?: React.ReactNode;
  footer?: React.ReactNode;
  /** A failed save or create, shown above the footer (ModalActionError takes focus). */
  error?: string | null;
  /** The error's heading, e.g. "The tax code was not created". */
  errorTitle?: string;
  size?: 'md' | 'lg' | 'xl';
  onClose: () => void;
  closeDisabled?: boolean;
  className?: string;
  children: React.ReactNode;
}) {
  const titleId = React.useId();
  const dialogRef = React.useRef<HTMLDivElement>(null);

  // Outside the Maintenance layout (warehouse routes, Settings panels, editor pages) no dialog
  // manager runs, so the dialog takes focus itself and gives it back when it closes.
  React.useEffect(() => {
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const frame = window.requestAnimationFrame(() => {
      const dialog = dialogRef.current;
      if (dialog && !dialog.contains(document.activeElement)) dialog.focus({ preventScroll: true });
    });
    return () => {
      window.cancelAnimationFrame(frame);
      const active = document.activeElement;
      if (opener?.isConnected && (!active || active === document.body)) opener.focus({ preventScroll: true });
    };
  }, []);

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div
      className="modal-overlay modal-overlay--solid directory-record-overlay"
      onMouseDown={event => {
        if (event.target === event.currentTarget && !closeDisabled) onClose();
      }}
    >
      <div
        ref={dialogRef}
        className={cx('modal directory-record-dialog', className)}
        data-size={size}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        onKeyDown={event => {
          // Escape closes, except where a menu or a native select is handling it.
          if (event.key !== 'Escape' || event.defaultPrevented || closeDisabled) return;
          const active = document.activeElement;
          if (active instanceof HTMLSelectElement || (active instanceof HTMLElement && active.closest('[role="menu"], [role="listbox"]'))) return;
          event.preventDefault();
          onClose();
        }}
      >
        <div className="modal__header directory-record-dialog__header">
          <div className="directory-record-dialog__heading">
            <h2 id={titleId} className="modal__title">{title}</h2>
            {subtitle ? <p className="directory-record-dialog__subtitle">{subtitle}</p> : null}
          </div>
          {status ? <div className="directory-record-dialog__status">{status}</div> : null}
          <button type="button" className="btn-close directory-record-dialog__close" aria-label="Close" onClick={onClose} disabled={closeDisabled}>
            <CloseIcon />
          </button>
        </div>
        {tabs ? <div className="directory-record-dialog__tabs">{tabs}</div> : null}
        <div className="modal__body">
          <div className="modal__body-content directory-record-dialog__body">{children}</div>
        </div>
        <ModalActionError message={error ?? null} title={errorTitle} />
        {footer ?<div className="modal__footer directory-record-dialog__footer">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  );
}

/**
 * Runs a dialog form's own Save from outside the form (e.g. the header's AutosaveStatus `onSave`
 * on an explicit-save dialog): submits it through its submit button, which may sit in the dialog
 * footer (`form=`), and does nothing while that button is disabled.
 */
export function requestDirectoryFormSubmit(formId: string): void {
  const form = document.getElementById(formId);
  if (!(form instanceof HTMLFormElement)) return;
  let submitter: HTMLButtonElement | null = null;
  for (const element of Array.from(form.elements)) {
    if (element instanceof HTMLButtonElement && element.type === 'submit') {
      submitter = element;
      break;
    }
  }
  if (submitter?.disabled) return;
  form.requestSubmit(submitter);
}

/* ------------------------------------------------------------------ states + footer */

export function DirectoryLoading({ children = 'Loading…' }: { children?: React.ReactNode }) {
  return <div className="directory-empty" role="status">{children}</div>;
}

export function DirectoryEmpty({ title, message, action }: {
  title: string;
  message?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="directory-empty">
      <h2>{title}</h2>
      {message ? <p>{message}</p> : null}
      {action}
    </div>
  );
}

/** A load or refresh failure; `retained` says the last loaded list is still shown. */
export function DirectoryError({ title, message, retained, onRetry, retrying }: {
  title: string;
  message: React.ReactNode;
  /** `true` for the default note, or the hub's own wording. */
  retained?: boolean | string;
  onRetry?: () => void;
  retrying?: boolean;
}) {
  return (
    <div className="directory-error" role="alert">
      <div>
        <strong>{title}</strong>
        <p>{message}</p>
        {retained ? <p>{retained === true ? 'The last loaded list is still shown below.' : retained}</p> : null}
      </div>
      {onRetry ? <button type="button" className="btn btn-outline sm" onClick={onRetry} disabled={retrying}>Try again</button> : null}
    </div>
  );
}

/** "Showing 50 of 82 matching locations" plus Load more (or a legend). */
export function DirectoryFooter({ summary, children }: { summary: React.ReactNode; children?: React.ReactNode }) {
  return <div className="directory-footer"><span>{summary}</span>{children}</div>;
}

export function DirectoryLegend({ children }: { children: React.ReactNode }) {
  return <span className="directory-legend">{children}</span>;
}

/**
 * `CardableTable` props for a settings table inside a Directory editor ("Settings tables" in
 * docs/maintenance-directory-layouts.md): always a table, no List/Grid switch, and a saved Grid
 * choice no longer applies. The page's CSS turns rows into labelled cards at 1100px and below.
 */
export const DIRECTORY_SETTINGS_TABLE_VIEW = {
  viewMode: 'list',
  onViewModeChange: () => undefined,
  showViewToggle: false,
} as const;

export const DIRECTORY_PAGE_SIZE = 50;

/**
 * Client-side "Load more" for lists already in memory: shows 50 rows, grows by 50, and resets
 * when `resetKey` changes (search, filters, sort). A secondary list inside an editor may pass a
 * smaller `pageSize`.
 */
export function useDirectoryPaging<Row>(rows: ReadonlyArray<Row>, resetKey: string, pageSize: number = DIRECTORY_PAGE_SIZE) {
  const [visible, setVisible] = React.useState(pageSize);
  React.useEffect(() => { setVisible(pageSize); }, [resetKey, pageSize]);
  const shown = React.useMemo(() => rows.slice(0, visible), [rows, visible]);
  const loadMore = React.useCallback(() => setVisible(count => count + pageSize), [pageSize]);
  return { shown, hasMore: visible < rows.length, loadMore };
}
